import { envConfig, getCurrentEnvironment, isDevelopment, isLocal } from './env.config';

// Okta Authentication Configuration for different environments
export interface OktaConfig {
  issuer: string;
  clientId: string;
  redirectUri: string;
  scopes: string[];
  pkce: boolean;
  disableHttpsCheck: boolean;
  postLogoutRedirectUri: string;
  // Token management configuration
  tokenManager?: {
    autoRenew?: boolean;
    autoRemove?: boolean;
    expireEarlySeconds?: number;
    storage?: string;
  };
  // Session management
  maxClockSkew?: number;
}

// Create Okta configuration from environment variables
const createOktaConfig = (): OktaConfig => {
  return {
    issuer: envConfig.oktaIssuer,
    clientId: envConfig.oktaClientId,
    redirectUri: envConfig.oktaRedirectUri,
    scopes: ['openid', 'profile', 'email', 'offline_access'],
    pkce: true,
    disableHttpsCheck: isDevelopment() || isLocal(), // Allow HTTP in development and local
    postLogoutRedirectUri: envConfig.oktaPostLogoutRedirectUri,
    // Token management configuration
    tokenManager: {
      autoRenew: envConfig.autoTokenRefreshEnabled, // Enable automatic token renewal
      autoRemove: true, // Automatically remove expired tokens
      expireEarlySeconds: envConfig.tokenRefreshBufferMinutes * 60, // Refresh tokens before expiration
      storage: 'localStorage', // Use localStorage for token storage
    },
    // Allow some clock skew for token validation
    maxClockSkew: 300, // 5 minutes
  };
};

// Export the configuration for the current environment
export const oktaConfig: OktaConfig = createOktaConfig();

// Export environment name for debugging
export const currentEnvironment = getCurrentEnvironment();

// Validation function to ensure all required config values are present
export const validateOktaConfig = (config: OktaConfig): boolean => {
  const requiredFields: (keyof OktaConfig)[] = ['issuer', 'clientId', 'redirectUri'];
  
  for (const field of requiredFields) {
    if (!config[field]) {
      console.error(`Okta configuration missing: ${field}`);
      console.error(`Current environment: ${currentEnvironment}`);
      console.error('Please ensure the correct .env file is loaded for your environment.');
      return false;
    }
  }
  
  return true;
};

// Helper function to get base URL for different environments
export const getBaseUrl = (): string => {
  return envConfig.baseUrl || window.location.origin;
};

// Helper function to get API base URL
export const getApiBaseUrl = (): string => {
  return envConfig.apiBaseUrl;
};

// Utility function to clear all Okta-related storage
export const clearOktaStorage = (): void => {
  try {
    // Clear all localStorage
    localStorage.clear();
    
    // Clear all sessionStorage
    sessionStorage.clear();
    
    // Clear specific Okta storage items (in case clear() doesn't work)
    const keysToRemove = [
      'okta-token-storage',
      'okta-shared-transaction-storage',
      'okta-original-uri',
      'okta-cache-storage',
      'okta-pkce-storage'
    ];
    
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
    
    console.log('Okta storage cleared successfully');
  } catch (error) {
    console.error('Error clearing Okta storage:', error);
  }
};

// Utility function to clear only problematic session data (preserves other app data)
export const clearProblematicOktaSession = (): void => {
  try {
    // Clear only Okta-specific storage items that might cause conflicts
    const keysToRemove = [
      'okta-token-storage',
      'okta-shared-transaction-storage',
      'okta-cache-storage',
      'okta-pkce-storage',
      'okta-original-uri'
    ];
    
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
    
    // Also clear any keys that start with 'okta-' to catch any other storage
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('okta-')) {
        localStorage.removeItem(key);
      }
    });
    
    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith('okta-')) {
        sessionStorage.removeItem(key);
      }
    });
    
    console.log('Problematic Okta session data cleared');
  } catch (error) {
    console.error('Error clearing problematic Okta session:', error);
  }
};

// Helper function to check if current URL has OAuth error parameters
export const hasOAuthError = (): { hasError: boolean; error?: string; errorDescription?: string } => {
  const urlParams = new URLSearchParams(window.location.search);
  const hasError = urlParams.has('error');
  
  if (hasError) {
    return {
      hasError: true,
      error: urlParams.get('error') || undefined,
      errorDescription: urlParams.get('error_description') || undefined
    };
  }
  
  return { hasError: false };
};